import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Card, Divider, RadixTable, TColumn } from '@features/ui';
import { periodOptions } from '@features/admin/reports/reportData';
import { toast } from 'react-toastify';
import {
  ReportPeriod,
  TReportConversionRateByPaymentSystemBreakdownData,
  TReportConversionRateByPaymentSystemBreakdownParams,
} from '@src/types/reports';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import dayjs from 'dayjs';
import { downloadFile } from '@src/lib/utils';
import {
  exportConversionRateByPaymentSystemBreakdown,
  getConversionRateByPaymentSystemBreakdown,
} from '@src/services/reports';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    payload: TReportConversionRateByPaymentSystemBreakdownData;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className={
          'bg-card-stat p-2.5 rounded-1 text-xs border-1 border-[#3D3D3D] rounded-[8px] flex flex-col items-center min-w-[135px] gap-y-1'
        }
        style={{
          boxShadow: '0px 7px 13px 0px #B6D72C14',
        }}
      >
        <p className="text-[#B6D72C] text-xl font-semibold">
          {payload[0]?.value}%
        </p>
        <p className="text-[#999999] text-[10px]">
          {payload[0]?.payload?.payment_system ?? '-'}
        </p>
        <p className="text-white text-xs">
          Conversion Rate: {payload[0]?.value}%
        </p>
      </div>
    );
  }

  return null;
};

export const ConversionRate: FC = () => {
  const [data, setData] = useState<
    TReportConversionRateByPaymentSystemBreakdownData[]
  >([]);

  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    enableComparisons: false,
  });

  const sortedData = useMemo(
    () => [...data].sort((a, b) => b.conversion_rate - a.conversion_rate),
    [data]
  );

  const columns = useMemo(
    (): TColumn<TReportConversionRateByPaymentSystemBreakdownData>[] => [
      {
        title: 'Payment System',
        render: (item) => (
          <p className="text-white">{item?.payment_system ?? '-'}</p>
        ),
      },
      {
        title: 'Conversion Rate (%)',
        className: 'text-right',
        render: (item) => (
          <p className="text-white">
            {Number(item?.conversion_rate ?? 0).toFixed(2)}%
          </p>
        ),
      },
    ],
    []
  );

  const buildPayload =
    (): TReportConversionRateByPaymentSystemBreakdownParams => {
      return {
        start_date: filters.startDate
          ? dayjs(filters.startDate).format('YYYY-MM-DD')
          : null,
        end_date: filters.endDate
          ? dayjs(filters.endDate).format('YYYY-MM-DD')
          : null,
      };
    };

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const payload = buildPayload();
      const response = await getConversionRateByPaymentSystemBreakdown(payload);

      setData(response);
      toast.success('Report generated successfully!');
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async (format: 'csv' | 'xlsx') => {
    try {
      const payload = buildPayload();
      const response = await exportConversionRateByPaymentSystemBreakdown(
        payload,
        format
      );

      if (format === 'csv') {
        const csvBlob = new Blob([response.data], { type: 'text/csv' });
        const csvUrl = URL.createObjectURL(csvBlob);
        const link = document.createElement('a');

        link.href = csvUrl;
        link.download = 'conversion_rate_by_payment_system_breakdown.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(csvUrl);
      } else {
        downloadFile(response.data, 'conversion_rate_by_payment_system_breakdown');
      }

      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  useEffect(() => {
    handleRunReport();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Conversion Rate By Payment System Breakdown
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          dateRangeValue={filters.valueStart}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedPeriod={filters.selectedPeriod}
          showCompareToPreviousPeriod={false}
          showCompareToSamePeriodPreviousYear={false}
          showGroupByFilter={false}
          onDateRangeChange={filters.setValueStart}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <BarChart data={sortedData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="payment_system"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `${value}%`}
              />
              <Tooltip
                content={<CustomTooltip />}
                cursor={{ fill: 'transparent' }}
              />
              <Legend iconType="rect" wrapperStyle={{ fontSize: '14px' }} />
              <Bar
                barSize={60}
                dataKey="conversion_rate"
                fill="#B6D72C"
                name="Conversion Rate (%)"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <RadixTable columns={columns} data={sortedData} />
          <Divider className="my-4" />
          <div className="flex flex-wrap justify-end gap-3">
            <Button
              className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
              variant="outline"
              onClick={() => handleExportReport('csv')}
            >
              Download CSV
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
